'use client';

import { Flex, Text, InlineUnsafe, Separator } from '@/_components';
import { Question } from './components/Question';
import styles from './QuestionnaireSection.module.css';
import {
  getTriggeredQuestions,
  mapQuestionsToLevels,
} from '@/_utils/questions';
import { QuestionLevel } from './components/QuestionLevel';
import { QuestionForm } from './components/QuestionForm';
import { useMemo, Fragment, type FC, useContext } from 'react';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import type { QuestionnaireSectionProps } from './QuestionnaireSection.types';

export const QuestionnaireSection: FC<QuestionnaireSectionProps> = ({
  isDisabled,
  isReviewSection,
  mode,
  questions,
  instruction,
}) => {
  const { glossaries } = useContext(FirmSettingsContext);

  const questionLevels = useMemo(
    () => mapQuestionsToLevels(questions || []),
    [questions],
  );

  return (
    <>
      {instruction && (
        <Flex isColumn={true} gap={12} padding={12}>
          <>
            <Text type="div">
              <InlineUnsafe text={instruction} isExtremelyUnsafe={true} />
            </Text>
            <Separator isLight={true} />
          </>
        </Flex>
      )}
      <Flex className={styles.questionnaireSection} isColumn={true}>
        {questionLevels.map((question1, key1) => {
          const {
            hasTriggeredQuestions: hasSecondLevel,
            questions: secondLevelQuestions,
          } = getTriggeredQuestions(question1);

          return (
            <QuestionLevel level={1} key={key1}>
              <Question
                number={`${key1 + 1}.`}
                level={1}
                id={question1.id}
                glossaries={glossaries}
                isReviewSection={isReviewSection}
                isOptional={question1.optional}
                status={question1.status}
                title={question1.text}
                priorYearValue={question1.prior_year_value}
              >
                <QuestionForm
                  question={question1}
                  isDisabled={isDisabled}
                  mode={mode}
                />
              </Question>
              {hasSecondLevel && (
                <>
                  <QuestionLevel level={2} key={key1 + 'level2'}>
                    {secondLevelQuestions.map((question2, key2) => {
                      const {
                        hasTriggeredQuestions: hasThirdLevel,
                        questions: thirdLevelQuestions,
                      } = getTriggeredQuestions(question2);

                      return (
                        <Fragment key={key1 + key2}>
                          <Question
                            number={`${key1 + 1}.${key2 + 1}`}
                            level={2}
                            id={question2.id}
                            glossaries={glossaries}
                            isLastChild={
                              key2 === secondLevelQuestions.length - 1
                            }
                            isReviewSection={isReviewSection}
                            isOptional={question2.optional}
                            status={question2.status}
                            title={question2.text}
                            priorYearValue={question2.prior_year_value}
                          >
                            <QuestionForm
                              question={question2}
                              mode={mode}
                              isDisabled={isDisabled}
                            />
                          </Question>
                          {hasThirdLevel && (
                            <QuestionLevel level={3}>
                              {thirdLevelQuestions.map((question3, key3) => {
                                return (
                                  <Question
                                    key={key3}
                                    number={`${key1 + 1}.${key2 + 1}.${key3 + 1}`}
                                    level={3}
                                    glossaries={glossaries}
                                    hasSecondLevelContinuation={
                                      key2 !== secondLevelQuestions.length - 1
                                    }
                                    isReviewSection={isReviewSection}
                                    id={question3.id}
                                    isOptional={question3.optional}
                                    status={question3.status}
                                    title={question3.text}
                                    priorYearValue={question3.prior_year_value}
                                  >
                                    <QuestionForm
                                      question={question3}
                                      mode={mode}
                                      isDisabled={isDisabled}
                                    />
                                  </Question>
                                );
                              })}
                            </QuestionLevel>
                          )}
                        </Fragment>
                      );
                    })}
                  </QuestionLevel>
                </>
              )}
            </QuestionLevel>
          );
        })}
      </Flex>
    </>
  );
};
